import DOMPurify from "dompurify";

import translateNested from "app/pages/sales-intelligence/common-components/i18n/translateNested";
import type { TranslatableNameWithType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import {
    getSelectionReplacements,
    isPartialFilterName,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";
import type { Translatable } from "app/pages/sales-intelligence/types";
import { formatSelectionOverflowHtml } from "app/pages/sales-intelligence/sub-modules/signals/utils/signalSelectionOverflow";

const HTML_ESCAPE_PATTERN = /[&<>"']/g;

const HTML_ESCAPE_REPLACEMENTS: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
};

const CONTAINS_BOLD_HTML_PATTERN = /<\s*b\b[^>]*>/i;

const SIGNAL_NAME_HTML_SANITIZE_OPTIONS = { ALLOWED_TAGS: ["b"], ALLOWED_ATTR: [] as string[] };

export function escapeHtml(text: string): string {
    return text.replace(HTML_ESCAPE_PATTERN, (character) => HTML_ESCAPE_REPLACEMENTS[character] ?? character);
}

export function containsBoldHtml(text: string): boolean {
    return CONTAINS_BOLD_HTML_PATTERN.test(text);
}

/** Allows only `<b>` markup produced by signal name formatters. */
export function sanitizeSignalNameHtml(html: string): string {
    return DOMPurify.sanitize(html, SIGNAL_NAME_HTML_SANITIZE_OPTIONS);
}

export function boldHtml(text: string): string {
    return `<b>${escapeHtml(text)}</b>`;
}

/** Resolved translatable text wrapped in `<b>` for signal copy HTML rendering. */
export function boldTranslatable(translatable: Readonly<Translatable>): string {
    const nested = translateNested(translatable);

    if (containsBoldHtml(nested)) {
        return sanitizeSignalNameHtml(nested);
    }

    return boldHtml(nested);
}

/** Bold filter selection copy; bolds the visible option and `+N` overflow count for partial selections. */
export function boldFilterSelectionName(filterName: Readonly<TranslatableNameWithType>): string {
    const replacements = getSelectionReplacements(filterName);
    const remainingOptions = replacements?.remainingOptions ?? 0;

    if (filterName.type === "any") {
        return translateNested(filterName.translatable);
    }

    if (isPartialFilterName(filterName)) {
        const option = replacements?.option ?? filterName.translatable.key;

        return `${boldHtml(String(option))}${formatSelectionOverflowHtml(remainingOptions)}`;
    }

    if (filterName.type === "singular") {
        return boldHtml(String(filterName.translatable.key));
    }

    return boldTranslatable(filterName.translatable);
}

/** Translatable whose `key` is HTML-safe signal copy (for `translateNested` / `dangerouslySetInnerHTML`). */
export function boldFilterSelectionTranslatable(
    filterName: Readonly<TranslatableNameWithType>,
): Readonly<Translatable> {
    if (filterName.type === "any") {
        return filterName.translatable;
    }

    return { key: boldFilterSelectionName(filterName) };
}
